"use strict";

exports.__esModule = true;
exports.Advert = void 0;
var _CompanionAds = require("./CompanionAds");
var _CompanionCreative = require("./CompanionCreative");
var _YoLog = require("../Utils/YoLog");
var _ConversionUtils = require("../Utils/ConversionUtils");
var _NonLinearAds = require("./NonLinearAds");
var _StringUtils = require("../Utils/StringUtils");
var _AdvertEventHandler2 = require("../Public/AdvertEventHandler");
var _AdVerification = require("./AdVerification");
var _AdvertWrapper = require("./AdvertWrapper");
var _IndustryIcon = require("./IndustryIcon");
var _InteractiveCreative = require("./InteractiveCreative");
var _LinearCreative = require("./LinearCreative");
var _NonLinearCreative = require("./NonLinearCreative");
var _Resource = require("./Resource");
var _VASTProperty = require("./VASTProperty");
var _XmlNode = require("../Core/XmlNode");
var _TrackingReport = require("./TrackingReport");
function _createForOfIteratorHelperLoose(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (it) return (it = it.call(o)).next.bind(it); if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; return function () { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _inheritsLoose(subClass, superClass) { subClass.prototype = Object.create(superClass.prototype); subClass.prototype.constructor = subClass; _setPrototypeOf(subClass, superClass); }
function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }
/**
 * Represents an advert object.
 *
 * The Advert encapsulates the data from the `<Ad>` and `<inline>` element of a VAST document: its
 * attributes, creatives and positional information.
 * 
 * Through {@link AdvertEventHandler} it supports the ability for a client app to signal error, viewable and impression events.
 * 
 * @hideconstructor
 * @see {@link AdvertEventHandler}, which this class implements
 */
var Advert = /*#__PURE__*/function (_AdvertEventHandler) {
  _inheritsLoose(Advert, _AdvertEventHandler);
  Advert.buildMapString = function buildMapString(map) {
    var s = "";
    if (map.size > 0) {
      for (var _iterator = _createForOfIteratorHelperLoose(map), _step; !(_step = _iterator()).done;) {
        var _step$value = _step.value,
          key = _step$value[0],
          value = _step$value[1];
        s += "\n " + key + "=" + value;
      }
    }
  };
  function Advert(advertData) {
    var _this;
    _this = _AdvertEventHandler.call(this) || this;

    /** @private */
    _this.startMillis = 0;
    /** @private */
    _this.identifier = "";
    /** @private */
    _this.active = false;
    var id = advertData.id;
    if (id) {
      var components = id.split("_YO_");
      if (components.length === 2) {
        _this.identifier = components[0];
        _this.mediaIdentifier = components[1];
      }
    }

    /** @private */
    _this.properties = advertData.properties ? advertData.properties : [];
    /** @private */
    _this.linearCreative = advertData.linearCreative;
    /** @private */
    _this.nonLinearAds = advertData.nonLinearAds ? advertData.nonLinearAds : new _NonLinearAds.NonLinearAds();
    /** @private */
    _this.companionAds = advertData.companionAds ? advertData.companionAds : new _CompanionAds.CompanionAds();
    /** @private */
    _this.impressions = advertData.impressions;
    /** @private */
    _this.errors = advertData.errors;
    /** @private */
    _this.viewableImpressions = advertData.viewableImpressions ? advertData.viewableImpressions : new Map();
    /** @private */
    _this.adVerifications = advertData.adVerifications ? advertData.adVerifications : [];
    /** @private */
    _this.sequence = advertData.sequence;
    /** @private */
    _this.adType = advertData.adType;
    /** @private */
    _this.nonLinear = advertData.nonLinear;
    /** @private */
    _this.filler = advertData.filler;
    /** @private */
    _this.trackingSchedule = new Map();
    if (!advertData.topLevelWrapper || !advertData.topLevelWrapper.isValid()) {
      /** @private */
      _this.wrapper = null;
    } else {
      _this.wrapper = advertData.topLevelWrapper;
    }
    /** @private */
    _this.extensions = advertData.extensions;
    /** @private */
    _this.broker = null;
    /** @private */
    _this.macroSubstitutions = new Map();
    /** @private */
    _this.earlyReturn = false;
    return _this;
  }

  /**
   * Adds a entry to the client macro substitutions map such that any tracking URL containing
   * a macro matching the key will be substituted with the entry's value.
   * 
   * <b>NOTE</b>: the key should not be enclosed in square brackets.
   * @param {string} key the macro name
   * @param {string} value the macro substitution value
   */
  var _proto = Advert.prototype;
  _proto.addMacroSubstitution = function addMacroSubstitution(key, value) {
    this.macroSubstitutions.set(key, value);
  };
  _proto.addTrackingScheduleEntry = function addTrackingScheduleEntry(type, proportion) {
    var delay = proportion * this.linearCreative.getDuration();
    while (this.trackingSchedule.has(delay)) {
      ++delay;
    }
    this.trackingSchedule.set(delay, type);
  };
  _proto.adjustForEarlyReturn = function adjustForEarlyReturn(playhead) {
    var duration = playhead - this.startMillis;
    this.setDuration(duration);
    var trackingMap = this.getTrackingSchedule();
    for (var _iterator2 = _createForOfIteratorHelperLoose(trackingMap.keys()), _step2; !(_step2 = _iterator2()).done;) {
      var k = _step2.value;
      if (k > duration) {
        this.removeFromTrackingSchedule(k);
      }
    }
    this.earlyReturn = true;
  };
  _proto.clearTrackingSchedule = function clearTrackingSchedule() {
    this.trackingSchedule.clear();
  };
  _proto.containsCreative = function containsCreative(creative) {
    if (creative instanceof _LinearCreative.LinearCreative) {
      return creative === this.linearCreative;
    } else if (creative instanceof _NonLinearCreative.NonLinearCreative) {
      return this.nonLinearAds.containsCreative(creative);
    } else if (creative instanceof _CompanionCreative.CompanionCreative) {
      return this.companionAds.containsCreative(creative);
    }
    return false;
  };
  _proto.containsIcon = function containsIcon(icon) {
    for (var _iterator3 = _createForOfIteratorHelperLoose(this.linearCreative.getIndustryIcons()), _step3; !(_step3 = _iterator3()).done;) {
      var ii = _step3.value;
      if (ii === icon) {
        return true;
      }
    }
    return false;
  }

  /** Returns an array of verification objects used to execute third-party measurement code for an advert.
   * @return {AdVerification[]} an array of `AdVerification` objects associated with the advert
   */;
  _proto.getAdVerifications = function getAdVerifications() {
    return Object.assign([], this.adVerifications);
  }

  /**
   * @return {string} the advert's ad type as a `string`
   */;
  _proto.getAdType = function getAdType() {
    return this.adType;
  };
  _proto.getCompanionAds = function getCompanionAds() {
    return this.companionAds;
  }

  /**
   * Returns an array of `CompanionCreative` objects of the specified resource type
   * @param {ResourceType} type the resource type
   * @return {CompanionCreative[]} an `array` of `CompanionCreative`s
   */;
  _proto.getCompanionAdsByType = function getCompanionAdsByType(type) {
    return this.companionAds.getCompanionsByType(type);
  }

  /**
   * The required attribute for the `CompanionCreative` objects contained within this Ad.
   * 
   * Possible values are 'all', 'any', 'none' or blank.
   * @return {string} the `required` attribute as a `string`
   */;
  _proto.getCompanionRequired = function getCompanionRequired() {
    return this.companionAds.getRequired();
  }

  /**
   * @return {number} the duration of the advert in milliseconds
   */;
  _proto.getDuration = function getDuration() {
    if (this.nonLinear) {
      return 0;
    }
    return this.linearCreative.getDuration();
  };
  _proto.getEnd = function getEnd() {
    return this.startMillis + this.linearCreative.getDuration();
  }

  /**
   * @return {XmlNode} the XML VAST extensions for this advert as an `XmlNode` or null if there are no extensions.
   */;
  _proto.getExtensions = function getExtensions() {
    return this.extensions;
  }

  /**
   * @return {string} the identifier of the advert as a `string`
   */;
  _proto.getIdentifier = function getIdentifier() {
    return this.identifier;
  };
  _proto.getImpressions = function getImpressions(shouldRemove) {
    if (!this.impressions) {
      return null;
    }
    var report = new _TrackingReport.TrackingReport(this.impressions.trackingEvent, this.impressions.trackingUrls);
    if (shouldRemove) {
      this.impressions = null;
    }
    return report;
  }

  /**
   * @return {IndustryIcon[]} an array of `IndustryIcon` objects associated with the advert
   */;
  _proto.getIndustryIcons = function getIndustryIcons() {
    return this.linearCreative.getIndustryIcons();
  }

  /**
   * @return {InteractiveCreative} the `InteractiveCreative` of the advert or null if one is not present
   */;
  _proto.getInteractiveCreative = function getInteractiveCreative() {
    return this.linearCreative.getInteractiveCreative();
  };
  _proto.isTruncated = function isTruncated() {
    return this.earlyReturn;
  }

  /**
   * @return {AdvertWrapper} an `AdvertWrapper` representing the advert's wrapper data as a one-way 'linked list'
   */;
  _proto.getLineage = function getLineage() {
    return this.wrapper;
  }

  /**
   * @return {LinearCreative} the `LinearCreative` of the advert
   */;
  _proto.getLinearCreative = function getLinearCreative() {
    return this.linearCreative;
  }

  /**
   * @return {string[]} an `array` of macro substitutions as `string`s
   */;
  _proto.getMacroSubstitutions = function getMacroSubstitutions() {
    return this.macroSubstitutions;
  }

  /**
   * 
   * @return {string} the Yospace identifier associated with the ingested advert media
   */;
  _proto.getMediaIdentifier = function getMediaIdentifier() {
    return this.mediaIdentifier;
  };
  _proto.getNonLinearAds = function getNonLinearAds() {
    return this.nonLinearAds;
  }

  /**
   * @param {ResourceType} type the resource type
   * @return {NonLinearCreative[]} an `array` of `NonLinearCreative` objects for the advert
   */;
  _proto.getNonLinearCreativesByType = function getNonLinearCreativesByType(type) {
    return this.nonLinearAds.getNonLinearCreativesByType(type);
  }

  /**
   * Returns the inline properties for the advert. The properties are represented as YOVASTProperty objects. Possible properties are:
   *
   * - AdSystem (Defines the source ad server of the advert)
   * - AdTitle (Defines the common name of the advert)
   * - AdServingId (Defines an identifier used to compare impression-level data across systems)
   * - Category (Defines the category of the advert content)
   * - Description (Defines a longer description of the advert)
   * - Advertiser (Defines the advertiser name)
   * - Pricing (Defines the pricing element of the advert)
   * - Survey (Defines a URI to any resource file having to do with collecting survey data)
   * - Expires (Defines the number of seconds in which the ad is valid for execution)
   *
   * @return {VASTProperty[]} an `array` of `VASTProperty` objects
   */;
  _proto.getProperties = function getProperties() {
    return Object.assign([], this.properties);
  }

  /**
   * Returns a named inline property for the advert if it exists. Possible properties are:
   *
   * - AdSystem (Defines the source ad server of the advert)
   * - AdTitle (Defines the common name of the advert)
   * - AdServingId (Defines an identifier used to compare impression-level data across systems)
   * - Category (Defines the category of the advert content)
   * - Description (Defines a longer description of the advert)
   * - Advertiser (Defines the advertiser name)
   * - Pricing (Defines the pricing element of the advert)
   * - Survey (Defines a URI to any resource file having to do with collecting survey data)
   * - Expires (Defines the number of seconds in which the ad is valid for execution)
   *
   * @param {string} name the name of the property
   * @return {VASTProperty} a `VASTProperty` object or `null` if not found
   */;
  _proto.getProperty = function getProperty(name) {
    for (var _iterator4 = _createForOfIteratorHelperLoose(this.properties), _step4; !(_step4 = _iterator4()).done;) {
      var prop = _step4.value;
      if (prop.getName() === name) {
        return prop;
      }
    }
    return null;
  };
  _proto.getProportion = function getProportion(key) {
    var proportion = 0;
    var split = key.split("-");
    if (split.length !== 2) {
      _YoLog.YoLog.w("Badly formed progress event in VAST/VMAP: " + key);
      proportion = -1;
    } else if (split[1].indexOf("%") !== -1) {
      var d = parseFloat(split[1].substring(0, split[1].length - 1));
      proportion = d ? d / 100.0 : -1;
      if (proportion < 0) {
        _YoLog.YoLog.w("Badly formed percentage string in VAST/VMAP: " + split[1]);
      }
    } else {
      proportion = parseFloat((0, _ConversionUtils.timeStringToMillis)(split[1], -1) / this.linearCreative.getDuration());
    }
    return proportion;
  }

  /**
   * Returns the natural playback time remaining for the advert in milliseconds
   * @param {number} playhead the current playhead position in milliseconds
   * @return {number} the remaining time in milliseconds
   */;
  _proto.getRemainingTime = function getRemainingTime(playhead) {
    var end = this.startMillis + this.linearCreative.getDuration();
    var delta = Math.min(end - playhead, this.linearCreative.getDuration());
    return Math.max(0, delta);
  }

  /**
   * @return {number} the sequence of the advert as a `number`
   */;
  _proto.getSequence = function getSequence() {
    return this.sequence;
  }

  /**
   * @return {number} the value of skip offset for the advert, in milliseconds. If the VAST skip offset is not defined then this method
   * returns `-1`, signifying that the advert cannot be skipped.
   */;
  _proto.getSkipOffset = function getSkipOffset() {
    return this.linearCreative.getSkipOffset();
  }

  /**
   * @return {number} the start playhead position of the advert in milliseconds
   */;
  _proto.getStart = function getStart() {
    return this.startMillis;
  };
  _proto.getTrackingReports = function getTrackingReports(type) {
    var reports = [];

    // linear creative
    var report = this.linearCreative.getTrackingReport(type);
    if (report) {
      reports.push(report);
    }

    // nonlinear
    report = this.nonLinearAds.getTrackingReport(type);
    if (report) {
      reports.push(report);
    }
    return reports;
  };
  _proto.getTrackingReportLinear = function getTrackingReportLinear(type) {
    if (!this.linearCreative) {
      return undefined;
    }
    return this.linearCreative.getTrackingReport(type);
  };
  _proto.getTrackingReportNonLinear = function getTrackingReportNonLinear(type) {
    if (!this.nonLinearAds) {
      return undefined;
    }
    return this.nonLinearAds.getTrackingReport(type);
  };
  _proto.getTrackingSchedule = function getTrackingSchedule() {
    return this.trackingSchedule;
  };
  _proto.getViewableImpressions = function getViewableImpressions() {
    return this.viewableImpressions;
  }

  /**
   * @return {boolean} whether this advert is active i.e. whether it has been watched through to completion.
   */;
  _proto.isActive = function isActive() {
    return this.active;
  }

  /**
   * @return {boolean} whether this advert represents filler content
   */;
  _proto.isFiller = function isFiller() {
    return this.filler;
  }

  /**
   * @return {boolean} whether this advert represents nonlinear content
   */;
  _proto.isNonLinear = function isNonLinear() {
    return this.nonLinear;
  };
  _proto.isValid = function isValid() {
    if (this.nonLinear) {
      return this.linearCreative ? false : true;
    } else {
      return !(0, _StringUtils.isNullOrEmpty)(this.mediaIdentifier) && this.linearCreative !== null;
    }
  };
  _proto.onErrorEvent = function onErrorEvent(errorCode) {
    if (this.errors) {
      // add the macro substitution if required
      if (errorCode) {
        this.addMacroSubstitution("ERRORCODE", errorCode.toString());
      }

      // error beacons are fired at the discretion of the client application
      this.broker.fireTrackingReport(this.errors);
    }
  };
  _proto.onImpressionEvent = function onImpressionEvent() {
    if (!this.nonLinear) {
      _YoLog.YoLog.w("Warning: Advert is not nonlinear - can't signal ImpressionEvent");
      return;
    }
    var report = this.getImpressions(false);
    if (report) {
      this.broker.fireTrackingReport(report);
    }
  };
  _proto.onViewableEvent = function onViewableEvent(event) {
    var eventStr = Advert.viewableEventMap.get(event);
    var report = this.viewableImpressions.get(eventStr);
    if (report) {
      // publisher determined viewability, therefore there are no conditions for firing beacon
      this.broker.fireTrackingReport(report);
    }
  };
  _proto.postParse = function postParse(broker) {
    var _this2 = this;
    this.broker = broker;
    if (!this.nonLinear) {
      // add the 'standard' tracking schedule items
      this.addTrackingScheduleEntry("loaded", 0);
      this.addTrackingScheduleEntry("start", 0);
      this.addTrackingScheduleEntry("firstQuartile", 0.25);
      this.addTrackingScheduleEntry("midpoint", 0.5);
      this.addTrackingScheduleEntry("thirdQuartile", 0.75);
      this.addTrackingScheduleEntry("complete", 1.0);

      // get any progress events from the linear, modify name to 'progress-[offset-in-millis] and add offsets to tracking schedule
      for (var _iterator5 = _createForOfIteratorHelperLoose(this.linearCreative.getTrackingMap()), _step5; !(_step5 = _iterator5()).done;) {
        var _step5$value = _step5.value,
          key = _step5$value[0],
          value = _step5$value[1];
        if (key.indexOf("progress") !== -1) {
          var proportion = this.getProportion(key, value);
          if (proportion >= 0) {
            this.addTrackingScheduleEntry(key, proportion);
          }
        }
      }

      // now sort
      this.trackingSchedule = new Map(Array.from(this.trackingSchedule).sort(function (a, b) {
        return a[0] - b[0];
      }));
    }

    // provision AdVerification
    for (var _iterator6 = _createForOfIteratorHelperLoose(this.adVerifications), _step6; !(_step6 = _iterator6()).done;) {
      var adVerification = _step6.value;
      adVerification.postParse(this.broker, function (event) {
        _this2.addMacroSubstitution("REASON", event.getPayload());
      });
    }

    // provision non-linear creatives
    this.nonLinearAds.postParse();

    // provision interactive creative
    if (this.linearCreative) {
      this.linearCreative.initialiseInteractiveCreative(this.nonLinearAds.getNonLinearCreatives());
    }

    // prefetch resources as required
    this.prefetchResources(broker.getSessionProperties());
    this.active = true;
  };
  _proto.prefetchResources = function prefetchResources(properties) {
    if (properties.getPrefetchResources()) {
      this.nonLinearAds.prefetchResources(properties.getResourceTimeout());
      this.companionAds.prefetchResources(properties.getResourceTimeout());
    }
  };
  _proto.removeFromTrackingSchedule = function removeFromTrackingSchedule(key) {
    this.trackingSchedule["delete"](key);
  }

  /**
   * Removes the specified macro substitution if present
   * @param {string} key the macro name
   */;
  _proto.removeMacroSubstitution = function removeMacroSubstitution(key) {
    this.macroSubstitutions["delete"](key);
  };
  _proto.removeTrackingSchedule = function removeTrackingSchedule() {
    this.trackingSchedule.clear();
  };
  _proto.setDuration = function setDuration(duration) {
    if (this.linearCreative) {
      this.linearCreative.setDuration(duration);
    }
  };
  _proto.setInactive = function setInactive() {
    this.active = false;
  };
  _proto.setStart = function setStart(start) {
    this.startMillis = start;
  };
  _proto.toString = function toString() {
    if (this.isFiller()) {
      return "\n--- Advert ---\n - Filler duration: " + this.getDuration();
    }
    var impressions = "\n* Impression(s):";
    if (this.impressions) {
      for (var _iterator7 = _createForOfIteratorHelperLoose(this.impressions.getTrackingUrls()), _step7; !(_step7 = _iterator7()).done;) {
        var trackingUrl = _step7.value;
        impressions += trackingUrl;
      }
    } else {
      impressions += "NONE";
    }
    var viewableImpressions = "\n* Viewable Impression(s):";
    viewableImpressions += Advert.buildMapString(this.viewableImpressions);
    var adVerifications = "\n * Ad Verification(s):";
    adVerifications += (0, _StringUtils.toIndentedString)(this.adVerifications);
    var errors = "\n Error(s):";
    if (this.errors) {
      for (var _iterator8 = _createForOfIteratorHelperLoose(this.errors.getTrackingUrls()), _step8; !(_step8 = _iterator8()).done;) {
        var error = _step8.value;
        errors += (0, _StringUtils.toIndentedString)("\n - " + error);
      }
    } else {
      errors += "NONE";
    }
    var advertString = "\n--- Advert ---\n ID:";
    advertString += this.mediaIdentifier;
    advertString += "(";
    advertString += this.identifier;
    advertString += ")";
    advertString += " duration:";
    advertString += this.getDuration();
    advertString += " sequence:";
    advertString += this.sequence;
    advertString += " adtype:";
    advertString += this.adType;
    for (var _iterator9 = _createForOfIteratorHelperLoose(this.properties), _step9; !(_step9 = _iterator9()).done;) {
      var prop = _step9.value;
      advertString += "\n" + (0, _StringUtils.toIndentedString)(prop);
    }
    if (this.extensions) {
      advertString += "\n * Extensions:";
      advertString += "\n" + (0, _StringUtils.toIndentedString)(this.getExtensions());
    }
    if (this.wrapper) {
      advertString += "\n * Lineage -\n";
      advertString += (0, _StringUtils.toIndentedString)(this.wrapper);
    }
    advertString += (0, _StringUtils.toIndentedString)(impressions);
    advertString += (0, _StringUtils.toIndentedString)(viewableImpressions);
    advertString += (0, _StringUtils.toIndentedString)(adVerifications);
    advertString += (0, _StringUtils.toIndentedString)(errors);
    if (!this.nonLinear) {
      advertString += (0, _StringUtils.toIndentedString)(this.linearCreative);
    }
    advertString += (0, _StringUtils.toIndentedString)(this.nonLinearAds);
    advertString += (0, _StringUtils.toIndentedString)(this.companionAds);
    return advertString;
  };
  return Advert;
}(_AdvertEventHandler2.AdvertEventHandler);
/** @private */
exports.Advert = Advert;
Advert.viewableEventMap = new Map();
Advert.viewableEventMap.set(_AdvertEventHandler2.ViewableEvent.VIEWABLE, "Viewable");
Advert.viewableEventMap.set(_AdvertEventHandler2.ViewableEvent.NOT_VIEWABLE, "NotViewable");
Advert.viewableEventMap.set(_AdvertEventHandler2.ViewableEvent.VIEW_UNDETERMINED, "ViewUndetermined");